import { useQueryClient } from '@tanstack/react-query';
import { type ErrorComponentProps, DefaultGlobalNotFound, isRedirect, useRouter } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@worldcoo/ui/components/button';
import { AlertCircle } from 'lucide-react';
import { useServerFn } from '@tanstack/react-start';
import { extractErrorInfo, logErrorFn } from '@/lib/data';
import { useEphemeralStore } from '@/lib/app-store';

export function DefaultCatchBoundary(props: ErrorComponentProps) {
	if ('isNotFound' in props.error) {
		return <DefaultGlobalNotFound />;
	}

	if (isRedirect(props.error)) {
		return <RedirectHandler redirectError={props.error} />;
	}

	return <CatchBoundary {...props} />;
}

function RedirectHandler(props: { redirectError: unknown }) {
	const router = useRouter();

	useEffect(() => {
		if (isRedirect(props.redirectError)) {
			router.navigate(props.redirectError.options).catch((err) => {
				console.error('Failed to navigate after redirect:', err);
			});
		}
	}, [router, props.redirectError]);

	return null;
}

export const VERSION_SKEW_RELOAD_KEY = 'worldcoo:version-skew-reload';
export const VERSION_SKEW_RELOAD_COOLDOWN_MS = 30_000;

/**
 * Check if we can safely reload for a version skew error.
 * Returns false if we already reloaded recently (within cooldown), preventing infinite reload loops
 * that can happen during ECS rolling deployments when sticky sessions route to mismatched tasks.
 */
function canReloadForVersionSkew() {
	if (typeof window === 'undefined') return false;
	const lastReload = window.sessionStorage.getItem(VERSION_SKEW_RELOAD_KEY);
	return !lastReload || Date.now() - parseInt(lastReload) > VERSION_SKEW_RELOAD_COOLDOWN_MS;
}

function CatchBoundary(props: ErrorComponentProps) {
	const queryClient = useQueryClient();
	const logError = useServerFn(logErrorFn);
	const { t } = useLingui();
	const clearCheckoutCache = useEphemeralStore((state) => state.clearCheckoutCache);
	const hasLoggedError = useRef(false);

	const errorMessage = props.error.message ?? '';
	const isModuleLoadError = errorMessage.includes('error loading dynamically imported module');
	const isStaleServerFnError =
		errorMessage.includes('Server function info not found') ||
		errorMessage.includes('Server function module not resolved') ||
		errorMessage.includes('Server function module export not resolved') ||
		errorMessage.includes('expected content-type header to be set');
	const isVersionSkewError = isModuleLoadError || isStaleServerFnError;
	const shouldReloadForVersionSkew = isVersionSkewError && canReloadForVersionSkew();

	console.error('DefaultCatchBoundary Error:', props.error);

	// Log error to backend
	// If the error was derived from an update that is not retrocompatible (a version skew)
	// this will bust the cache on memory and immediately sync with disk
	useEffect(() => {
		if (hasLoggedError.current) return;
		hasLoggedError.current = true;

		queryClient.clear();
		clearCheckoutCache();

		if (shouldReloadForVersionSkew) {
			window.sessionStorage.setItem(VERSION_SKEW_RELOAD_KEY, String(Date.now()));
			window.location.reload();
			return;
		}

		if (isVersionSkewError) return;

		const errorInfo = extractErrorInfo(props.error);
		logError({
			data: {
				route: window.location.pathname,
				name: errorInfo.name,
				message: errorInfo.message,
				stack: errorInfo.stack,
			},
		}).catch((logErr) => {
			console.error('Failed to log error to backend:', logErr);
		});
	}, [queryClient, props.error, logError, clearCheckoutCache, shouldReloadForVersionSkew, isVersionSkewError]);

	if (shouldReloadForVersionSkew) return null;

	return (
		<div className="flex flex-col gap-4 text-center">
			<div className="bg-destructive/10 mx-auto flex size-12 items-center justify-center rounded-full">
				<AlertCircle className="text-destructive size-6" />
			</div>

			<div className="flex flex-col gap-2">
				<h2 className="text-xl font-semibold">{t`Something went wrong`}</h2>
				<p className="text-muted-foreground text-sm">
					{t`We're sorry, but something unexpected happened. Please try again or refresh the page.`}
				</p>
			</div>

			{import.meta.env.DEV && props.error instanceof Error && (
				<details className="text-left">
					<summary className="text-muted-foreground cursor-pointer text-sm font-medium">{t`Technical details`}</summary>
					<div className="bg-muted mt-2 rounded-md p-3">
						<p className="font-mono text-xs wrap-break-word">{props.error.message}</p>
						{props.error.stack && (
							<pre className="text-muted-foreground mt-2 max-h-40 overflow-auto font-mono text-xs">
								{props.error.stack}
							</pre>
						)}
					</div>
				</details>
			)}

			<div className="flex flex-col gap-2">
				<Button onClick={() => window.location.reload()} variant="default">
					{t`Try again`}
				</Button>
				<Button onClick={() => (window.location.href = '/')} variant="outline">
					{t`Go to homepage`}
				</Button>
			</div>
		</div>
	);
}
